import { useEffect, useRef, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { api } from '../lib/api';
import { useAuth, type SessionUser } from './AuthContext';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
          }) => void;
          renderButton: (
            el: HTMLElement,
            opts: Record<string, string | number>,
          ) => void;
        };
      };
    };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

function loadGsi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Sign-In')));
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(script);
  });
}

export function Login() {
  const { setUser } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { googleClientId } = await api.get<{ googleClientId: string }>('/api/auth/config');
        if (!googleClientId) {
          setError('Google login is not configured on the server.');
          return;
        }
        await loadGsi();
        if (cancelled) return;
        window.google!.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (resp: { credential: string }) => {
            setBusy(true);
            setError(null);
            try {
              const { user } = await api.post<{ user: SessionUser }>('/api/auth/login', {
                credential: resp.credential,
              });
              setUser(user);
            } catch (e: unknown) {
              setError((e as Error).message || 'Login failed');
            } finally {
              setBusy(false);
            }
          },
        });
        if (buttonRef.current) {
          buttonRef.current.replaceChildren();
          window.google!.accounts.id.renderButton(buttonRef.current, {
            theme: 'filled_black',
            size: 'large',
            shape: 'pill',
            text: 'signin_with',
            type: 'standard',
            width: 280,
          });
        }
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error).message || 'Could not initialize Google Sign-In');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setUser]);

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandMark size="lg" />
        <p className="muted">Sign in with your Google account to continue.</p>
        {busy ? (
          <p className="muted">Signing you in…</p>
        ) : (
          <div className="login-gsi">
            <div className="login-gsi-face" aria-hidden="true">
              <svg className="login-gsi-g" viewBox="0 0 48 48" aria-hidden="true">
                <path
                  fill="#FFC107"
                  d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.3-.1-3.5z"
                />
                <path
                  fill="#FF3D00"
                  d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
                />
                <path
                  fill="#4CAF50"
                  d="M24 44c5.2 0 10-1.7 13.6-4.7l-6.3-5.3C29.2 35.6 26.7 36.5 24 36.5c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"
                />
                <path
                  fill="#1976D2"
                  d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.2-4.2 5.5l6.3 5.3C40.4 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"
                />
              </svg>
              Sign in with Google
            </div>
            <div className="login-btn-wrap" ref={buttonRef} />
          </div>
        )}
        {error && <p className="login-error">{error}</p>}
        <p className="muted login-hint">Only @riskcherry.com accounts that have been granted access can sign in.</p>
      </div>
    </div>
  );
}
